/**
 * DatabaseConfigDrawer — Wide drawer for editing server database config.
 *
 * Organized into 3 sections:
 *   1. Serial Settings (in/out port, baud, delimiter)
 *   2. General (TTL, check-in times)
 *   3. Environmental (processing toggle, sample rate, temp thresholds, battery)
 *
 * Reads current values from configData array, builds a payload of
 * { config_value, config_id, modified_by } items for the API save.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/** Lookup a config row by name (case-insensitive), returning { config_id, config_value } */
function getConfigVal(data, name) {
  const key = name.toLowerCase();
  const row = data?.find((r) => (r.config_name || '').toLowerCase() === key);
  return { id: row?.config_id || 0, value: row?.config_value ?? '' };
}

/** Standard baud rate options */
const BAUD_OPTIONS = [300, 1200, 2400, 4800, 9600, 14400, 19200, 38400, 57600, 115200];

const DatabaseConfigDrawer = ({ open, onClose, onSave, saving, configData, userId }) => {
  // Ref to hold the latest configData (avoids stale closures in useEffect)
  const configRef = useRef(configData);
  configRef.current = configData;

  // Extract current values for submit handler (needs config_ids)
  const init = useMemo(() => ({
    inPort: getConfigVal(configData, 'IN_SERIAL_PORTNAME'),
    outPort: getConfigVal(configData, 'OUT_SERIAL_PORTNAME'),
    inBaud: getConfigVal(configData, 'IN_SERIAL_BAUD'),
    outBaud: getConfigVal(configData, 'OUT_SERIAL_BAUD'),
    inDelim: getConfigVal(configData, 'IN_SERIAL_MSGDELIMITER'),
    outDelim: getConfigVal(configData, 'OUT_SERIAL_MSGDELIMITER'),
    ttl: getConfigVal(configData, 'CONFIG_TTL'),
    checkinTime: getConfigVal(configData, 'IN_CHECKINTIME'),
    locCheckinTime: getConfigVal(configData, 'CONFIG_INOLOCCHECKINTIME'),
    envProcessing: getConfigVal(configData, 'CONFIG_ENVIRONMENTAL_PROCESSING'),
    sampleRate: getConfigVal(configData, 'CONFIG_ENV_SAMPLE_RATE'),
    highTemp: getConfigVal(configData, 'CONFIG_INOHIGHTEMP'),
    lowTemp: getConfigVal(configData, 'CONFIG_INOLOWTEMP'),
    lowBattery: getConfigVal(configData, 'CONFIG_INOLOWBAT'),
  }), [configData]);

  // Form state
  const [inPort, setInPort] = useState('');
  const [outPort, setOutPort] = useState('');
  const [inBaud, setInBaud] = useState('');
  const [outBaud, setOutBaud] = useState('');
  const [inDelim, setInDelim] = useState('');
  const [outDelim, setOutDelim] = useState('');
  const [ttl, setTtl] = useState('');
  const [checkinTime, setCheckinTime] = useState('');
  const [locCheckinTime, setLocCheckinTime] = useState('');
  const [envProcessing, setEnvProcessing] = useState(false);
  const [sampleRate, setSampleRate] = useState('');
  const [highTemp, setHighTemp] = useState('');
  const [lowTemp, setLowTemp] = useState('');
  const [lowBattery, setLowBattery] = useState('');

  // Populate form from configData only when drawer opens (false → true).
  // Uses a ref for configData to avoid stale closures and prevent
  // the 5s polling cycle from resetting edits while the drawer is open.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      const data = configRef.current;
      const get = (name) => getConfigVal(data, name).value;
      setInPort(get('IN_SERIAL_PORTNAME'));
      setOutPort(get('OUT_SERIAL_PORTNAME'));
      setInBaud(get('IN_SERIAL_BAUD'));
      setOutBaud(get('OUT_SERIAL_BAUD'));
      setInDelim(get('IN_SERIAL_MSGDELIMITER'));
      setOutDelim(get('OUT_SERIAL_MSGDELIMITER'));
      setTtl(get('CONFIG_TTL'));
      setCheckinTime(get('IN_CHECKINTIME'));
      setLocCheckinTime(get('CONFIG_INOLOCCHECKINTIME'));
      const envVal = get('CONFIG_ENVIRONMENTAL_PROCESSING');
      setEnvProcessing((envVal || '').toString().toUpperCase() === 'YES');
      const srVal = get('CONFIG_ENV_SAMPLE_RATE');
      setSampleRate(srVal ? Math.round(parseInt(srVal, 10) / 60) : '');
      setHighTemp(get('CONFIG_INOHIGHTEMP'));
      setLowTemp(get('CONFIG_INOLOWTEMP'));
      setLowBattery(get('CONFIG_INOLOWBAT'));
    }
    prevOpen.current = open;
  });

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    // Build config items array — only include items that have valid config_ids
    const items = [];
    const push = (cfgInit, value) => {
      if (cfgInit.id) {
        items.push({ config_value: value, config_id: cfgInit.id, modified_by: userId });
      }
    };

    push(init.inPort, inPort);
    push(init.outPort, outPort);
    push(init.inBaud, inBaud);
    push(init.outBaud, outBaud);
    push(init.inDelim, inDelim);
    push(init.outDelim, outDelim);
    push(init.ttl, ttl);
    push(init.checkinTime, checkinTime);
    push(init.locCheckinTime, locCheckinTime);
    push(init.envProcessing, envProcessing ? 'YES' : 'NO');
    push(init.sampleRate, sampleRate ? (parseInt(sampleRate, 10) * 60).toString() : '0');
    push(init.highTemp, highTemp);
    push(init.lowTemp, lowTemp);
    push(init.lowBattery, lowBattery);

    onSave(items);
  }, [
    init, userId, onSave,
    inPort, outPort, inBaud, outBaud, inDelim, outDelim,
    ttl, checkinTime, locCheckinTime,
    envProcessing, sampleRate, highTemp, lowTemp, lowBattery,
  ]);

  return (
    <div className={`dp-drawer dp-drawer--wide${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Database Config</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Serial Settings */}
        <p className="pp-drawer-section">Serial Settings</p>
        <div className="pp-drawer-grid">
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">In Port</label>
            <input className="dp-drawer__input" value={inPort} onChange={(e) => setInPort(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Out Port</label>
            <input className="dp-drawer__input" value={outPort} onChange={(e) => setOutPort(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">In Baud</label>
            <select className="dp-drawer__select" value={inBaud} onChange={(e) => setInBaud(e.target.value)}>
              <option value="">—</option>
              {BAUD_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Out Baud</label>
            <select className="dp-drawer__select" value={outBaud} onChange={(e) => setOutBaud(e.target.value)}>
              <option value="">—</option>
              {BAUD_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">In Delimiter</label>
            <input className="dp-drawer__input" value={inDelim} onChange={(e) => setInDelim(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Out Delimiter</label>
            <input className="dp-drawer__input" value={outDelim} onChange={(e) => setOutDelim(e.target.value)} />
          </div>
        </div>

        {/* General */}
        <p className="pp-drawer-section" style={{ marginTop: 'var(--space-4)' }}>General</p>
        <div className="pp-drawer-grid">
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">TTL (seconds)</label>
            <input className="dp-drawer__input" type="number" value={ttl} onChange={(e) => setTtl(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Resident Check-in (sec)</label>
            <input className="dp-drawer__input" type="number" value={checkinTime} onChange={(e) => setCheckinTime(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Location Check-in (sec)</label>
            <input className="dp-drawer__input" type="number" value={locCheckinTime} onChange={(e) => setLocCheckinTime(e.target.value)} />
          </div>
        </div>

        {/* Environmental */}
        <p className="pp-drawer-section" style={{ marginTop: 'var(--space-4)' }}>Environmental</p>
        <div className="pp-drawer-grid">
          <div className="dp-drawer__field dp-drawer__field--row" style={{ gridColumn: '1 / -1' }}>
            <input
              type="checkbox"
              className="dp-checkbox"
              id="env-processing"
              checked={envProcessing}
              onChange={(e) => setEnvProcessing(e.target.checked)}
            />
            <label htmlFor="env-processing" className="dp-drawer__label dp-drawer__label--inline">
              Environmental Processing
            </label>
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Sample Rate (min)</label>
            <input className="dp-drawer__input" type="number" value={sampleRate} onChange={(e) => setSampleRate(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">High Temp (°F)</label>
            <input className="dp-drawer__input" type="number" value={highTemp} onChange={(e) => setHighTemp(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Low Temp (°F)</label>
            <input className="dp-drawer__input" type="number" value={lowTemp} onChange={(e) => setLowTemp(e.target.value)} />
          </div>
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Low Battery (V)</label>
            <input className="dp-drawer__input" type="number" step="0.1" value={lowBattery} onChange={(e) => setLowBattery(e.target.value)} />
          </div>
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Config'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DatabaseConfigDrawer;
